import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useTranslation from 'v2/layouts/AccountWorkspaceLayout/components/CertificationsSection/hooks/useTranslation';
import type { ApplicatorCertificationResponse } from 'v2/layouts/AccountWorkspaceLayout/components/CertificationsSection/types';
import { getIsEditPanelOpen } from 'v2/layouts/AccountWorkspaceLayout/routing';

import useApplicatorCertifications from 'hooks/useApplicatorCertifications';
import useNavigation from 'hooks/useNavigation';
import usePageRoutes from 'hooks/usePageRoutes';
import useToast from 'hooks/useToast';

const useCertificationsTable = () => {
    const { deleteApplicatorCertification, fetchApplicatorCertifications } =
        useApplicatorCertifications();

    const { pathname } = useLocation();
    const { navigate } = useNavigation();
    const { accountNewApplicatorCertification, editApplicatorCertificationPage } = usePageRoutes();
    const { broadcastToast } = useToast();
    const { DELETE_ERROR, DELETE_SUCCESS, LOAD_ERROR } = useTranslation();

    const [certifications, setCertifications] = useState<ApplicatorCertificationResponse[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const [pendingDeleteCertificationId, setPendingDeleteCertificationId] = useState<string | null>(
        null
    );

    const loadPage = useCallback(
        async (pageToken?: string | null) => {
            const response = await fetchApplicatorCertifications(pageToken);

            setCertifications(currentCertifications =>
                pageToken ? [...currentCertifications, ...response.objects] : response.objects
            );
            setHasNextPage(response.hasNext);
            setNextPageToken(response.nextPageToken ?? null);
        },
        [fetchApplicatorCertifications]
    );

    useEffect(() => {
        const loadFirstPage = async () => {
            try {
                await loadPage();
            } catch {
                broadcastToast({ text: LOAD_ERROR, type: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        void loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isEditPanelOpen = getIsEditPanelOpen(pathname);
    const wasEditPanelOpen = useRef(isEditPanelOpen);

    // This tab opts out of the workspace remount, so it picks up an add or an
    // edit itself. The rows already on screen stay put while the fresh page is
    // fetched: isLoading is deliberately left alone.
    useEffect(() => {
        const refreshFirstPage = async () => {
            try {
                await loadPage();
            } catch {
                broadcastToast({ text: LOAD_ERROR, type: 'error' });
            }
        };

        if (wasEditPanelOpen.current && !isEditPanelOpen) {
            void refreshFirstPage();
        }

        wasEditPanelOpen.current = isEditPanelOpen;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditPanelOpen]);

    const handleLoadMore = useCallback(async () => {
        if (!hasNextPage || isFetchingMore) {
            return;
        }

        setIsFetchingMore(true);

        try {
            await loadPage(nextPageToken);
        } catch {
            broadcastToast({ text: LOAD_ERROR, type: 'error' });
        } finally {
            setIsFetchingMore(false);
        }
    }, [LOAD_ERROR, broadcastToast, hasNextPage, isFetchingMore, loadPage, nextPageToken]);

    const handleAdd = useCallback(
        () => navigate(accountNewApplicatorCertification),
        [accountNewApplicatorCertification, navigate]
    );

    const handleEdit = useCallback(
        (certificationId: string) => navigate(editApplicatorCertificationPage(certificationId)),
        [editApplicatorCertificationPage, navigate]
    );

    const handleDeleteRequest = useCallback(
        (certificationId: string) => setPendingDeleteCertificationId(certificationId),
        []
    );

    const handleDeleteCancel = useCallback(() => setPendingDeleteCertificationId(null), []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!pendingDeleteCertificationId) {
            return;
        }

        try {
            await deleteApplicatorCertification(pendingDeleteCertificationId);
        } catch {
            broadcastToast({ text: DELETE_ERROR, type: 'error' });
            setPendingDeleteCertificationId(null);

            return;
        }

        setCertifications(currentCertifications =>
            currentCertifications.filter(
                certificationItem => certificationItem.id !== pendingDeleteCertificationId
            )
        );
        setPendingDeleteCertificationId(null);

        broadcastToast({ text: DELETE_SUCCESS, type: 'success' });
    }, [
        DELETE_ERROR,
        DELETE_SUCCESS,
        broadcastToast,
        deleteApplicatorCertification,
        pendingDeleteCertificationId
    ]);

    return {
        certifications,
        handleAdd,
        handleDeleteCancel,
        handleDeleteConfirm,
        handleDeleteRequest,
        handleEdit,
        handleLoadMore,
        hasNextPage,
        isDeleteConfirmOpen: pendingDeleteCertificationId !== null,
        isFetchingMore,
        isLoading
    };
};

export default useCertificationsTable;
